const DEFAULT_ROOT_PRESENTATION_DECLARATIONS = [
  'display: block',
  'width: 100%',
  'height: 100%',
  'max-width: 100%',
  'max-height: 100%',
  'object-fit: contain',
  'overflow: visible',
];

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

export const stripSvgProlog = (markup) => String(markup || '')
  .replace(/^\s*<\?xml[^>]*>\s*/i, '')
  .replace(/^\s*<!doctype[^>]*>\s*/i, '');

export const stripSvgNamespacePrefixes = (markup) => String(markup || '')
  .replace(/\sxmlns:svg="http:\/\/www\.w3\.org\/2000\/svg"/i, '')
  .replace(/<(\/?)svg:([A-Za-z][\w.-]*)/g, '<$1$2');

const normalizeSvgRootStyle = (styleValue, rootStyleDeclarations) => {
  const preservedRules = String(styleValue || '')
    .split(';')
    .map((rule) => rule.trim())
    .filter(Boolean)
    .filter((rule) => !/^(display|width|height|max-width|max-height|object-fit|overflow)\s*:/i.test(rule));

  return [...preservedRules, ...rootStyleDeclarations].join('; ');
};

const appendInlineSvgStyle = (doc, svg, extraCssText) => {
  const normalizedCss = String(extraCssText || '').trim();
  if (!normalizedCss) {
    return;
  }

  const style = doc.createElementNS(SVG_NAMESPACE, 'style');
  style.setAttribute('data-inline-svg-style', '1');
  style.textContent = normalizedCss;
  svg.appendChild(style);
};

const applySvgRootStyle = (svg, styleValue) => {
  const nextStyle = String(styleValue || '').trim();
  if (nextStyle) {
    svg.setAttribute('style', nextStyle);
    return;
  }

  svg.removeAttribute('style');
};

export const prepareInlineSvgMarkup = (rawSvgContent, options = {}) => {
  const fallbackMarkup = stripSvgProlog(rawSvgContent).trim();
  if (!fallbackMarkup) {
    return '';
  }

  const rootStyleDeclarations = Array.isArray(options.rootStyleDeclarations) && options.rootStyleDeclarations.length > 0
    ? options.rootStyleDeclarations
    : DEFAULT_ROOT_PRESENTATION_DECLARATIONS;
  const extraCssText = String(options.extraCssText || '').trim();

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    let normalizedMarkup = stripSvgNamespacePrefixes(fallbackMarkup);

    if (/<svg\b/i.test(normalizedMarkup)) {
      normalizedMarkup = normalizedMarkup.replace(
        /<svg([^>]*)>/i,
        `<svg$1 preserveAspectRatio="xMidYMid meet" style="${rootStyleDeclarations.join('; ')}" width="100%" height="100%">`
      );
    }

    if (extraCssText) {
      normalizedMarkup = normalizedMarkup.replace(
        /<svg([^>]*)>/i,
        `<svg$1><style data-inline-svg-style="1">${extraCssText}</style>`
      );
    }

    return normalizedMarkup;
  }

  try {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(fallbackMarkup, 'image/svg+xml');
    const parseError = parsedDocument.querySelector('parsererror');
    const svg = parsedDocument.documentElement;

    if (!parseError && svg && svg.nodeName.toLowerCase().includes('svg')) {
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.setAttribute('style', normalizeSvgRootStyle(svg.getAttribute('style'), rootStyleDeclarations));
      appendInlineSvgStyle(parsedDocument, svg, extraCssText);

      return stripSvgNamespacePrefixes(new XMLSerializer().serializeToString(svg));
    }
  } catch (error) {
    // Fall through to the string-based fallback below.
  }

  let normalizedMarkup = stripSvgNamespacePrefixes(fallbackMarkup).replace(
    /<svg([^>]*)>/i,
    `<svg$1 preserveAspectRatio="xMidYMid meet" style="${rootStyleDeclarations.join('; ')}" width="100%" height="100%">`
  );

  if (extraCssText) {
    normalizedMarkup = normalizedMarkup.replace(
      /<svg([^>]*)>/i,
      `<svg$1><style data-inline-svg-style="1">${extraCssText}</style>`
    );
  }

  return normalizedMarkup;
};

export const prepareEmbeddedSvgMarkup = (rawSvgContent, options = {}) => {
  const fallbackMarkup = stripSvgProlog(rawSvgContent).trim();
  if (!fallbackMarkup) {
    return '';
  }

  const extraCssText = String(options.extraCssText || '').trim();
  const x = Number(options.x);
  const y = Number(options.y);
  const width = Number(options.width);
  const height = Number(options.height);
  const preserveAspectRatio = String(options.preserveAspectRatio || 'xMidYMid meet');

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    let normalizedMarkup = stripSvgNamespacePrefixes(fallbackMarkup);
    normalizedMarkup = normalizedMarkup.replace(
      /<svg([^>]*)>/i,
      `<svg$1${Number.isFinite(x) ? ` x="${x}"` : ''}${Number.isFinite(y) ? ` y="${y}"` : ''}${Number.isFinite(width) ? ` width="${width}"` : ''}${Number.isFinite(height) ? ` height="${height}"` : ''} preserveAspectRatio="${preserveAspectRatio}">`
    );

    if (extraCssText) {
      normalizedMarkup = normalizedMarkup.replace(
        /<svg([^>]*)>/i,
        `<svg$1><style data-inline-svg-style="1">${extraCssText}</style>`
      );
    }

    return normalizedMarkup;
  }

  try {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(fallbackMarkup, 'image/svg+xml');
    const parseError = parsedDocument.querySelector('parsererror');
    const svg = parsedDocument.documentElement;

    if (!parseError && svg && svg.nodeName.toLowerCase().includes('svg')) {
      if (Number.isFinite(x)) {
        svg.setAttribute('x', String(x));
      } else {
        svg.removeAttribute('x');
      }

      if (Number.isFinite(y)) {
        svg.setAttribute('y', String(y));
      } else {
        svg.removeAttribute('y');
      }

      if (Number.isFinite(width) && width > 0) {
        svg.setAttribute('width', String(width));
      }

      if (Number.isFinite(height) && height > 0) {
        svg.setAttribute('height', String(height));
      }

      svg.setAttribute('preserveAspectRatio', preserveAspectRatio);
      applySvgRootStyle(svg, normalizeSvgRootStyle(svg.getAttribute('style'), []));
      appendInlineSvgStyle(parsedDocument, svg, extraCssText);

      return stripSvgNamespacePrefixes(new XMLSerializer().serializeToString(svg));
    }
  } catch (error) {
    // Fall through to string-based fallback.
  }

  let normalizedMarkup = stripSvgNamespacePrefixes(fallbackMarkup).replace(
    /<svg([^>]*)>/i,
    `<svg$1${Number.isFinite(x) ? ` x="${x}"` : ''}${Number.isFinite(y) ? ` y="${y}"` : ''}${Number.isFinite(width) ? ` width="${width}"` : ''}${Number.isFinite(height) ? ` height="${height}"` : ''} preserveAspectRatio="${preserveAspectRatio}">`
  );

  if (extraCssText) {
    normalizedMarkup = normalizedMarkup.replace(
      /<svg([^>]*)>/i,
      `<svg$1><style data-inline-svg-style="1">${extraCssText}</style>`
    );
  }

  return normalizedMarkup;
};

export const INLINE_SVG_DEFAULT_ROOT_PRESENTATION_DECLARATIONS = DEFAULT_ROOT_PRESENTATION_DECLARATIONS;
